import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { db } from '../firebase';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { FaUser, FaPhone, FaMapMarkerAlt, FaEdit, FaSave, FaTimes } from 'react-icons/fa';
import './ProfilePage.css';

const ProfilePage = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  const [userData, setUserData] = useState({
    name: currentUser?.name || '',
    email: currentUser?.email || '',
    phone: '',
    address: ''
  });
  
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingField, setEditingField] = useState(null);
  
  // Load user profile data from Firestore
  useEffect(() => {
    const loadUserProfile = async () => {
      if (currentUser?.uid) {
        try {
          const userDocRef = doc(db, 'users', currentUser.uid);
          const userDoc = await getDoc(userDocRef);
          
          if (userDoc.exists()) {
            const data = userDoc.data();
            setUserData({
              name: currentUser.name || data.name || '',
              email: currentUser.email || data.email || '',
              phone: data.phone || '',
              address: data.address || ''
            });
          } else {
            // Initialize with current user data
            setUserData({
              name: currentUser.name || '',
              email: currentUser.email || '',
              phone: '',
              address: ''
            });
          }
        } catch (error) {
          console.error('Error loading user profile:', error);
        }
      }
      setLoading(false);
    };
    
    loadUserProfile();
  }, [currentUser]);
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUserData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSave = async (field = null) => {
    if (!currentUser?.uid) return;
    
    setSaving(true);
    try {
      const userDocRef = doc(db, 'users', currentUser.uid);
      const dataToSave = {
        name: userData.name,
        email: userData.email,
        phone: userData.phone,
        address: userData.address,
        updatedAt: new Date().toISOString()
      };
      
      await setDoc(userDocRef, dataToSave, { merge: true });
      
      if (field) {
        setEditingField(null);
      } else {
        setIsEditing(false);
      }
      
      console.log('Profile updated successfully');
    } catch (error) {
      console.error('Error saving profile:', error);
      alert('Failed to save profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };
  
  const handleFieldEdit = (field) => {
    setEditingField(field);
  };
  
  const handleFieldCancel = (field) => {
    setEditingField(null);
    // Reset field to original value if needed
  };
  
  if (!currentUser) {
    navigate('/login');
    return null;
  }
  
  if (loading) {
    return (
      <>
        <Header />
        <div className="profile-page">
          <div className="profile-container">
            <div className="loading-spinner">Loading profile...</div>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header />
      <div className="profile-page">
        <div className="profile-container">
          <div className="profile-header">
            <h1>My Profile</h1>
            <p>Manage your personal information</p>
          </div>
          
          <div className="profile-content">
            <div className="profile-card">
              <div className="profile-info-section">
                <div className="profile-avatar">
                  {currentUser?.photoURL ? (
                    <img 
                      src={currentUser.photoURL} 
                      alt="Profile" 
                      className="profile-image-large"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div 
                    className="profile-placeholder-large" 
                    style={{ display: currentUser?.photoURL ? 'none' : 'flex' }}
                  >
                    <FaUser />
                  </div>
                </div>
                
                <div className="profile-details">
                  <div className="profile-detail">
                    <div className="detail-icon"><FaUser /></div>
                    <div className="detail-content">
                      <span className="detail-label">Full Name</span>
                      <span className="detail-value">{userData.name || 'Not provided'}</span>
                    </div>
                  </div>
                  
                  <div className="profile-detail">
                    <div className="detail-icon"><FaUser /></div>
                    <div className="detail-content">
                      <span className="detail-label">Email</span>
                      <span className="detail-value">{userData.email}</span>
                    </div>
                  </div>
                  
                  <div className="profile-detail editable">
                    <div className="detail-icon"><FaPhone /></div>
                    <div className="detail-content">
                      <span className="detail-label">Phone Number</span>
                      {editingField === 'phone' ? (
                        <div className="edit-field">
                          <input
                            type="tel"
                            name="phone"
                            value={userData.phone}
                            onChange={handleInputChange}
                            placeholder="Enter phone number"
                            className="inline-input"
                          />
                          <div className="edit-actions">
                            <button 
                              className="save-btn" 
                              onClick={() => handleSave('phone')}
                              disabled={saving}
                            >
                              <FaSave />
                            </button>
                            <button 
                              className="cancel-btn" 
                              onClick={() => handleFieldCancel('phone')}
                            >
                              <FaTimes />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="detail-display">
                          <span className="detail-value">
                            {userData.phone || 'Add phone number'}
                          </span>
                          <button 
                            className="edit-btn" 
                            onClick={() => handleFieldEdit('phone')}
                          >
                            <FaEdit />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="profile-detail editable">
                    <div className="detail-icon"><FaMapMarkerAlt /></div>
                    <div className="detail-content">
                      <span className="detail-label">Address</span>
                      {editingField === 'address' ? (
                        <div className="edit-field">
                          <textarea
                            name="address"
                            value={userData.address}
                            onChange={handleInputChange}
                            placeholder="Enter your address"
                            className="inline-textarea"
                            rows="3"
                          />
                          <div className="edit-actions">
                            <button 
                              className="save-btn" 
                              onClick={() => handleSave('address')}
                              disabled={saving}
                            >
                              <FaSave />
                            </button>
                            <button 
                              className="cancel-btn" 
                              onClick={() => handleFieldCancel('address')}
                            >
                              <FaTimes />
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="detail-display">
                          <span className="detail-value">
                            {userData.address || 'Add your address'}
                          </span>
                          <button 
                            className="edit-btn" 
                            onClick={() => handleFieldEdit('address')}
                          >
                            <FaEdit />
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default ProfilePage;
